// src/services/feedbackService.js
import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc,
  addDoc,
  limit,
  startAfter
} from 'firebase/firestore';
import { useTenant } from '../context/TenantContext';

class FeedbackService {
  constructor() {
    this.collectionName = 'feedback';
  }

  /**
   * Get all feedback for the current tenant
   */
  async getAllFeedback(tenantId, filters = {}) {
    try {
      const feedbackRef = collection(db, `tenants/${tenantId}/${this.collectionName}`);
      let q = query(feedbackRef, orderBy('submittedAt', 'desc'));

      // Apply filters
      if (filters.clinicianId) {
        q = query(q, where('clinicianId', '==', filters.clinicianId));
      }
      
      if (filters.rating) {
        q = query(q, where('rating', '==', filters.rating));
      }

      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const snapshot = await getDocs(q);
      const feedback = [];

      for (const docSnap of snapshot.docs) {
        const feedbackData = {
          id: docSnap.id,
          ...docSnap.data()
        };

        // Enrich with appointment and client data
        try {
          if (feedbackData.appointmentId) {
            const appointmentDoc = await getDoc(doc(db, `tenants/${tenantId}/appointments`, feedbackData.appointmentId));
            if (appointmentDoc.exists()) {
              feedbackData.appointment = appointmentDoc.data();
            }
          }

          if (feedbackData.clientId && feedbackData.clientId !== 'unknown') {
            const clientDoc = await getDoc(doc(db, `tenants/${tenantId}/clients`, feedbackData.clientId));
            if (clientDoc.exists()) {
              feedbackData.client = clientDoc.data();
            }
          }
        } catch (enrichError) {
          console.warn('Error enriching feedback data:', enrichError);
        }

        feedback.push(feedbackData);
      }

      return feedback;
    } catch (error) {
      console.error('Error getting feedback:', error);
      throw new Error(`Failed to get feedback: ${error.message}`);
    }
  }

  /**
   * Get feedback statistics
   */
  async getFeedbackStats(tenantId) {
    try {
      const feedbackRef = collection(db, `tenants/${tenantId}/${this.collectionName}`);
      const snapshot = await getDocs(feedbackRef);
      
      const stats = {
        totalFeedback: 0,
        averageRating: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        recommendationRate: 0,
        recentFeedback: 0,
        clinicianStats: {}
      };

      let totalRating = 0;
      let recommendCount = 0;
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        stats.totalFeedback++;
        
        // Rating stats
        if (data.rating) {
          totalRating += data.rating;
          stats.ratingDistribution[data.rating]++;
        }

        // Recommendation stats
        if (data.wouldRecommend === true) {
          recommendCount++;
        }

        // Recent feedback (last 30 days)
        if (data.submittedAt && new Date(data.submittedAt) > thirtyDaysAgo) {
          stats.recentFeedback++;
        }

        // Clinician stats
        if (data.appointment?.clinicianId) {
          const clinicianId = data.appointment.clinicianId;
          if (!stats.clinicianStats[clinicianId]) {
            stats.clinicianStats[clinicianId] = {
              totalFeedback: 0,
              totalRating: 0,
              averageRating: 0,
              recommendCount: 0
            };
          }
          
          stats.clinicianStats[clinicianId].totalFeedback++;
          if (data.rating) {
            stats.clinicianStats[clinicianId].totalRating += data.rating;
            stats.clinicianStats[clinicianId].averageRating = 
              stats.clinicianStats[clinicianId].totalRating / stats.clinicianStats[clinicianId].totalFeedback;
          }
          if (data.wouldRecommend === true) {
            stats.clinicianStats[clinicianId].recommendCount++;
          }
        }
      });

      stats.averageRating = stats.totalFeedback > 0 ? totalRating / stats.totalFeedback : 0;
      stats.recommendationRate = stats.totalFeedback > 0 ? (recommendCount / stats.totalFeedback) * 100 : 0;

      return stats;
    } catch (error) {
      console.error('Error getting feedback stats:', error);
      throw new Error(`Failed to get feedback stats: ${error.message}`);
    }
  }

  /**
   * Get feedback by clinician
   */
  async getFeedbackByClinician(tenantId, clinicianId) {
    try {
      const feedbackRef = collection(db, `tenants/${tenantId}/${this.collectionName}`);
      const q = query(
        feedbackRef, 
        where('appointment.clinicianId', '==', clinicianId),
        orderBy('submittedAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting clinician feedback:', error);
      throw new Error(`Failed to get clinician feedback: ${error.message}`);
    }
  }

  /**
   * Mark feedback as reviewed
   */
  async markAsReviewed(tenantId, feedbackId, reviewedBy) {
    try {
      const feedbackRef = doc(db, `tenants/${tenantId}/${this.collectionName}`, feedbackId);
      await updateDoc(feedbackRef, {
        reviewed: true,
        reviewedAt: new Date(),
        reviewedBy: reviewedBy
      });
    } catch (error) {
      console.error('Error marking feedback as reviewed:', error);
      throw new Error(`Failed to mark feedback as reviewed: ${error.message}`);
    }
  }

  /**
   * Add internal note to feedback
   */
  async addInternalNote(tenantId, feedbackId, note, addedBy) {
    try {
      const feedbackRef = doc(db, `tenants/${tenantId}/${this.collectionName}`, feedbackId);
      const feedbackDoc = await getDoc(feedbackRef);
      
      if (!feedbackDoc.exists()) {
        throw new Error('Feedback not found');
      }

      const currentData = feedbackDoc.data();
      const internalNotes = currentData.internalNotes || [];
      
      internalNotes.push({
        note,
        addedBy,
        addedAt: new Date(),
        id: Date.now().toString()
      });

      await updateDoc(feedbackRef, {
        internalNotes
      });
    } catch (error) {
      console.error('Error adding internal note:', error);
      throw new Error(`Failed to add internal note: ${error.message}`);
    }
  }

  /**
   * Get feedback summary for dashboard
   */
  async getDashboardSummary(tenantId) {
    try {
      const stats = await this.getFeedbackStats(tenantId);
      const recentFeedback = await this.getAllFeedback(tenantId, { limit: 5 });
      
      return {
        averageRating: stats.averageRating,
        totalFeedback: stats.totalFeedback,
        recommendationRate: stats.recommendationRate,
        recentFeedback: stats.recentFeedback,
        latestFeedback: recentFeedback
      };
    } catch (error) {
      console.error('Error getting dashboard summary:', error);
      throw new Error(`Failed to get dashboard summary: ${error.message}`);
    }
  }
}

const feedbackService = new FeedbackService();
export default feedbackService;
